import { useEffect, useRef, useState, type ReactNode } from 'react';
import { field } from '../create/Field';

/**
 * A choice that is SAVED on purpose, not on every `change` (Codex review of ticket 04):
 * Windows Chrome and Firefox fire `change` for each arrow key on a closed select, so a select
 * that saved on change wrote every option the keyboard passed through. The choice is a draft
 * until the select loses the focus or Enter is pressed; until then it says it is not saved.
 *
 * A choice still staged when the select unmounts (the panel closes) is saved then.
 *
 * The draft is dropped when the server's value changes (this save landing, or a colleague's),
 * by React's "adjust state when a prop changes", never by remounting: a remount would drop the
 * focus of a keyboard reader who just pressed Enter.
 */
export function useDraftChoice<V extends string>(value: V) {
  const [draft, setDraft] = useState<V | null>(null);
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(null);
  }
  const pending = draft !== null && draft !== value;
  return { shown: draft ?? value, draft: pending ? draft : null, setDraft };
}

export function FactSelect<V extends string>({
  id,
  value,
  options,
  onSave,
}: {
  id: string;
  value: V;
  options: readonly { value: V; label: string }[];
  onSave: (next: V) => void;
}) {
  const { shown, draft, setDraft } = useDraftChoice(value);
  const commit = () => {
    if (draft !== null) onSave(draft);
  };
  // A staged choice is saved when the field goes away too (the panel closed by Esc or a click
  // outside, which removes the select without a blur): "Not saved yet" never vanishes unsaved.
  // The last render's draft and save, read at unmount.
  const pending = useRef({ draft, onSave });
  pending.current = { draft, onSave };
  useEffect(
    () => () => {
      const { draft: staged, onSave: save } = pending.current;
      if (staged !== null) save(staged);
    },
    [],
  );
  return (
    <span className="flex flex-col items-stretch gap-1 sm:items-end">
      <select
        id={id}
        className={`${field} sm:w-56`}
        value={shown}
        onChange={(e) => setDraft(e.target.value as V)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
        }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {draft !== null ? <Unsaved>Not saved yet: press Enter or move on</Unsaved> : null}
    </span>
  );
}

export function Unsaved({ children }: { children: ReactNode }) {
  return (
    <span role="status" className="text-xs text-muted-foreground">
      {children}
    </span>
  );
}
